import { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Wrapper per setUser con logging
  const setUserWithLogging = (userData) => {
    console.log('👤 [AuthContext] setUser chiamato con:', userData);
    setUser(userData);
  };

  useEffect(() => {
    console.log('🔐 [AuthContext] Inizializzazione...');
    
    // Check for session token or JWT token first - only verify if we have credentials
    const storedSessionToken = localStorage.getItem('fantaclasse_session_token');
    const storedToken = localStorage.getItem('fantaclasse_token');
    const storedUser = localStorage.getItem('fantaclasse_user');
    
    console.log('🔐 [AuthContext] storedUser:', storedUser ? 'PRESENTE' : 'ASSENTE');
    console.log('🔐 [AuthContext] storedSessionToken:', storedSessionToken ? 'PRESENTE' : 'ASSENTE');
    console.log('🔐 [AuthContext] storedToken (JWT):', storedToken ? 'PRESENTE' : 'ASSENTE');
    
    // If we have NO credentials at all, don't even try to verify
    if (!storedSessionToken && !storedToken) {
      console.log('ℹ️ [AuthContext] Nessuna credenziale trovata, skip verifica');
      // Clean up any stale user data
      if (storedUser) {
        console.log('🧹 [AuthContext] Pulisco user data obsoleto senza credenziali');
        localStorage.removeItem('fantaclasse_user');
      }
      setIsLoading(false);
      return;
    }
    
    // We have credentials - load cached user first for instant UI
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log('🔐 [AuthContext] Parsed user data:', userData);
        setUser(userData);
      } catch (err) {
        console.error('❌ [AuthContext] Errore parsing user data:', err);
        localStorage.removeItem('fantaclasse_user');
      }
    }
    
    // Now verify the session/token with backend
    const verifyAuth = async () => {
      try {
        let response;
        
        if (storedSessionToken) {
          // Verify session token
          console.log('🔐 [AuthContext] Verifico session token...');
          response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include',
            headers: { 'X-Session-Token': storedSessionToken }
          });
        } else if (storedToken) {
          // Verify JWT token
          console.log('🔐 [AuthContext] Verifico JWT token...');
          const decoded = jwtDecode(storedToken);
          const now = Date.now() / 1000;
          
          if (decoded.exp <= now) {
            console.log('❌ [AuthContext] JWT token scaduto');
            throw new Error('Token expired');
          }
          
          setToken(storedToken);
          response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
        }
        
        if (response && response.ok) {
          const userData = await response.json();
          console.log('✅ [AuthContext] Sessione valida, dati aggiornati:', userData);
          setUser(userData);
          localStorage.setItem('fantaclasse_user', JSON.stringify(userData));
        } else {
          // Invalid credentials - clean up
          console.log('❌ [AuthContext] Credenziali non valide (status:', response?.status, ')');
          throw new Error('Invalid credentials');
        }
      } catch (err) {
        console.error('⚠️ [AuthContext] Verifica fallita:', err.message);
        // Clean up invalid credentials
        console.log('🧹 [AuthContext] Pulisco credenziali non valide');
        localStorage.removeItem('fantaclasse_user');
        localStorage.removeItem('fantaclasse_session_token');
        localStorage.removeItem('fantaclasse_token');
        setUser(null);
        setToken(null);
      } finally {
        console.log('🔐 [AuthContext] Inizializzazione completata, isLoading = false');
        setIsLoading(false);
      }
    };
    
    verifyAuth();
  }, []);

  const register = async (email, password, name) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registrazione fallita');
      }
      
      localStorage.setItem('fantaclasse_token', data.access_token);
      localStorage.setItem('fantaclasse_user', JSON.stringify(data.user));
      setToken(data.access_token);
      setUser(data.user);
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login fallito');
      }
      
      localStorage.setItem('fantaclasse_token', data.access_token);
      localStorage.setItem('fantaclasse_user', JSON.stringify(data.user));
      setToken(data.access_token);
      setUser(data.user);
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const storedSessionToken = localStorage.getItem('fantaclasse_session_token');
      const headers = {};
      if (storedSessionToken) {
        headers['X-Session-Token'] = storedSessionToken;
      }
      
      // Call backend logout to clear session
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    localStorage.removeItem('fantaclasse_token');
    localStorage.removeItem('fantaclasse_user');
    localStorage.removeItem('fantaclasse_session_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    setUser: setUserWithLogging,
    token,
    isLoading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
